import { useAtomValue } from 'jotai';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    BodySmallRegular,
    FruitfulCustomIcon,
    isNonMobileWeb,
    Separator,
    Sheet,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { SelectableRow } from '../../../../../../bills/components/common/BillRows';
import { currentlyEditingConfigAtom } from '../../../../../state/PaycheckRoutingAtoms';
import type { BankAccountDisplayDetail } from '../../../../../types/BankAccount';
import {
    type PaycheckRoutingRule,
    PaycheckRoutingRuleDescriptions,
    PaycheckRoutingRuleNames,
    PaycheckRoutingRuleType,
} from '../../../../../types/PaycheckRouting';
import RuleEditorButtons from '../common/RuleEditorButtons';
import RuleTargetAccount from '../common/RuleTargetAccount';
import PrimaryRuleAmount from './PrimaryRuleAmount';
import PrimaryRuleType from './PrimaryRuleType';

interface RuleEditorProps {
    bankAccounts: BankAccountDisplayDetail[];
    selectedRule: Partial<PaycheckRoutingRule>;
    updateSelectedRule: (rule: Partial<PaycheckRoutingRule>) => void;
    isEditingRule: boolean;
    onSave: () => void;
    onCancel: () => void;
    isOpen: boolean;
    onClose: () => void;
}

const PrimaryRuleEditorSheet = ({
    bankAccounts,
    selectedRule,
    updateSelectedRule,
    isEditingRule,
    onSave,
    onCancel,
    isOpen,
    onClose,
}: RuleEditorProps) => {
    const currentlyEditingConfig = useAtomValue(currentlyEditingConfigAtom);
    const [isRuleValid, setIsRuleValid] = useState(false);
    const [currentView, setCurrentView] = useState<
        'main' | 'selectAccount' | 'selectType'
    >('main');

    useEffect(() => {
        setIsRuleValid(
            (selectedRule.amount ?? 0) > 0 &&
                selectedRule.accountId != null &&
                selectedRule.type != null,
        );
    }, [selectedRule]);

    const rulePosition = currentlyEditingConfig?.rules?.primary.findIndex(
        (rule) => rule.id === selectedRule.id,
    );

    const editMode = isEditingRule ? 'Edit' : 'Add';

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={true}
            title={
                currentView === 'main'
                    ? `${editMode} rule`
                    : currentView === 'selectAccount'
                      ? 'Choose account'
                      : 'Select rule type'
            }
            headerLeftAction={
                currentView !== 'main' ? (
                    <BackButton
                        iconType={BackButtonIconType.ARROW_LEFT}
                        customBackFn={() => {
                            setCurrentView('main');
                        }}
                    />
                ) : undefined
            }
        >
            <YStack gap={16} minWidth={isNonMobileWeb ? 400 : undefined}>
                {currentView === 'main' ? (
                    <>
                        <RuleTargetAccount
                            bankAccounts={bankAccounts}
                            rule={selectedRule ?? {}}
                            updateTargetAccountId={(targetAccountId) => {
                                updateSelectedRule({
                                    ...selectedRule,
                                    accountId: targetAccountId,
                                });
                                setCurrentView('main');
                            }}
                            openSelect={() => {
                                setCurrentView('selectAccount');
                            }}
                            cardProps={{ paddingVertical: 11 }}
                        />
                        <PrimaryRuleType
                            selectedRuleType={selectedRule.type ?? null}
                            openSelect={() => {
                                setCurrentView('selectType');
                            }}
                        />
                        <PrimaryRuleAmount
                            amount={selectedRule.amount}
                            updateAmount={(amount) => {
                                updateSelectedRule({
                                    ...selectedRule,
                                    amount,
                                });
                            }}
                            selectedRuleType={selectedRule.type ?? null}
                        />
                        <View paddingBottom={rulePosition === -1 ? 20 : 0}>
                            <RuleEditorButtons
                                onSave={onSave}
                                onCancel={onCancel}
                                disableSave={!isRuleValid}
                            />
                        </View>
                    </>
                ) : currentView === 'selectAccount' ? (
                    <YStack separator={<Separator />}>
                        {bankAccounts.map((accountDetails, i) => {
                            const {
                                icon,
                                color,
                                nickname,
                                bankAccountId,
                                accountNumberMasked,
                            } = accountDetails;

                            return (
                                <View paddingVertical={8} key={i}>
                                    <SelectableRow
                                        isSelected={
                                            selectedRule.accountId ===
                                            bankAccountId
                                        }
                                        isMultiSelect={false}
                                        isFirstRow={i === 0}
                                        onSelect={() => {
                                            updateSelectedRule({
                                                ...selectedRule,
                                                accountId:
                                                    accountDetails.bankAccountId,
                                            });
                                            setCurrentView('main');
                                        }}
                                        rowContentComponent={
                                            <XStack
                                                alignItems="center"
                                                gap={16}
                                            >
                                                <FruitfulCustomIcon
                                                    customSize={{
                                                        iconSize: 20,
                                                        padding: 14,
                                                    }}
                                                    icon={icon}
                                                    color={color}
                                                />
                                                <YStack gap={4}>
                                                    <BodyMediumRegular>
                                                        {nickname}
                                                    </BodyMediumRegular>
                                                    <BodySmallRegular color="$gray1000">
                                                        Fruitful{' '}
                                                        {accountNumberMasked.slice(
                                                            -5,
                                                        )}
                                                    </BodySmallRegular>
                                                </YStack>
                                            </XStack>
                                        }
                                    />
                                </View>
                            );
                        })}
                    </YStack>
                ) : (
                    <YStack separator={<Separator />} gap={14}>
                        {[
                            PaycheckRoutingRuleType.FIXED,
                            PaycheckRoutingRuleType.MINIMUM_BALANCE,
                        ].map((rule, i) => {
                            return (
                                <SelectableRow
                                    key={i}
                                    isSelected={selectedRule.type === rule}
                                    isMultiSelect={false}
                                    isFirstRow={i === 0}
                                    onSelect={() => {
                                        updateSelectedRule({
                                            ...selectedRule,
                                            type: rule,
                                        });
                                        setCurrentView('main');
                                    }}
                                    rowContentComponent={
                                        <YStack padding={2} maxWidth="75%">
                                            <BodyMediumRegular>
                                                {PaycheckRoutingRuleNames[rule]}
                                            </BodyMediumRegular>
                                            <BodySmallRegular color="$gray1000">
                                                {
                                                    PaycheckRoutingRuleDescriptions[
                                                        rule
                                                    ]
                                                }
                                            </BodySmallRegular>
                                        </YStack>
                                    }
                                />
                            );
                        })}
                    </YStack>
                )}
            </YStack>
        </Sheet>
    );
};

export default PrimaryRuleEditorSheet;
